import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  type IPublicClientApplication,
} from "@azure/msal-browser";
import { silentRedirectUri } from "@/auth/authConfig";
import { liveConnection } from "@/api/liveConnection";
import { actingAs } from "@/api/actingAs";
import { deviceName } from "@/camera";
import { claimSignInAttempt, clearSignInAttempt } from "@/auth/session";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function signingInWouldFix(error: unknown): boolean {
  return error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError;
}

/**
 * A token for the API. When it can't be renewed quietly (the day-long refresh token has run out,
 * or the phone blocked the hidden renewal), the app goes back through Microsoft sign-in, which
 * signs the person straight back in while their Microsoft session lasts.
 */
async function acquireToken(msal: IPublicClientApplication, scope: string): Promise<string> {
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (!account) throw new ApiError(401, "No signed-in account.");

  try {
    const result = await msal.acquireTokenSilent({
      scopes: [scope],
      account,
      redirectUri: silentRedirectUri(),
    });
    clearSignInAttempt();
    return result.accessToken;
  } catch (error) {
    if (!signingInWouldFix(error) || !claimSignInAttempt()) throw error;
    await msal.acquireTokenRedirect({ scopes: [scope], account, loginHint: account.username });
    throw new ApiError(401, "Signing you in again…");
  }
}

/**
 * The API said the session is over: the account was disabled or deleted, or the password changed.
 * The cached tokens are no good, so sign in from scratch; Microsoft asks for the password if it
 * has to, or refuses.
 */
async function signInAgain(msal: IPublicClientApplication, scope: string): Promise<void> {
  if (!claimSignInAttempt()) return;
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  await msal.loginRedirect({
    scopes: [scope],
    prompt: "login",
    ...(account ? { loginHint: account.username } : {}),
  });
}

function sessionEnded(status: number, body: unknown): boolean {
  return (
    status === 401 &&
    typeof body === "object" &&
    body !== null &&
    (body as { title?: unknown }).title === "Signed out"
  );
}

function explain(body: unknown): string | undefined {
  if (typeof body !== "object" || body === null) return undefined;
  const problem = body as { detail?: unknown; errors?: Record<string, unknown> };
  if (typeof problem.errors === "object" && problem.errors !== null) {
    const first = Object.values(problem.errors)
      .flat()
      .find((entry): entry is string => typeof entry === "string" && entry.trim() !== "");
    if (first !== undefined) return first;
  }
  return typeof problem.detail === "string" && problem.detail.trim() !== ""
    ? problem.detail
    : undefined;
}

async function readBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

export function createApiClient(msal: IPublicClientApplication, scope: string, baseUrl = "") {
  async function request<T>(path: string, init: RequestInit = {}, json = true): Promise<T> {
    const token = await acquireToken(msal, scope);
    const headers = new Headers(init.headers);
    headers.set("Authorization", `Bearer ${token}`);
    if (json && init.body !== undefined) headers.set("Content-Type", "application/json");
    const live = liveConnection();
    if (live) headers.set("X-Live-Connection", live);
    const acting = actingAs();
    if (acting) headers.set("X-Act-As", acting.id);
    headers.set("X-Device", deviceName());

    const response = await fetch(`${baseUrl}${path}`, { ...init, headers });
    if (!response.ok) {
      const body = await readBody(response);
      if (sessionEnded(response.status, body)) await signInAgain(msal, scope);
      throw new ApiError(
        response.status,
        explain(body) ?? `The request failed with status ${String(response.status)}.`,
        body,
      );
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  return {
    /** Where the API is, for the live-updates hub. */
    baseUrl,
    token: () => acquireToken(msal, scope),
    get: <T>(path: string) => request<T>(path),
    post: <T>(path: string, body?: unknown) =>
      request<T>(path, {
        method: "POST",
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      }),
    patch: <T>(path: string, body?: unknown) =>
      request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
    put: <T>(path: string, body?: unknown) =>
      request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
    del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
    postForm: <T>(path: string, body: FormData) =>
      request<T>(path, { method: "POST", body }, false),
    /**
     * A form post that reports upload progress as a fraction from 0 to 1. fetch cannot observe an
     * upload, and a receipt photo over a phone connection is long enough to need a bar.
     */
    uploadForm: async <T>(
      path: string,
      body: FormData,
      onProgress: (fraction: number) => void,
    ): Promise<T> => {
      const token = await acquireToken(msal, scope);
      return new Promise<T>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${baseUrl}${path}`);
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        const live = liveConnection();
        if (live) xhr.setRequestHeader("X-Live-Connection", live);
        const acting = actingAs();
        if (acting) xhr.setRequestHeader("X-Act-As", acting.id);
        xhr.setRequestHeader("X-Device", deviceName());
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
        };
        xhr.onerror = () => reject(new ApiError(0, "The upload failed. Check your connection."));
        xhr.onload = () => {
          let parsed: unknown;
          try {
            parsed = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
          } catch {
            parsed = undefined;
          }
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(parsed as T);
            return;
          }
          reject(
            new ApiError(
              xhr.status,
              explain(parsed) ?? `The upload failed with status ${String(xhr.status)}.`,
              parsed,
            ),
          );
        };
        xhr.send(body);
      });
    },
    blob: async (path: string) => {
      const token = await acquireToken(msal, scope);
      const response = await fetch(`${baseUrl}${path}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(actingAs() ? { "X-Act-As": actingAs()?.id ?? "" } : {}),
        },
      });
      if (!response.ok) throw new ApiError(response.status, "Could not load the receipt.");
      return response.blob();
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
